import { shouldPolyfill } from '@formatjs/intl-segmenter/should-polyfill';
import { getFramework, type FormatBrushApplyContext, type FormatBrushImplementation } from '@/framework';
import { Layer } from '@/layers/layer';
import { type TextEditorPlugin } from '@/plugins/text-editor';
import { type TextSelectionRange } from '@/utils/text-kit';
import { type IServerGroupLayerProps } from '@/version-parse';
import {
    LayerType,
    type IAdvancedTextMaterial,
    type ILayerProps,
    type IListStyleMaterial,
    type IPosition,
    type IShapeExtension,
    type ISize,
    type ITextLayerProps,
    type ITextMark,
    type ITexture,
    type ListStyleStruct,
    type Optional,
    type TextAlignType,
    type TextEffectKind,
    type TextShapeType,
    type TextWritingType,
} from '@/version-types';
import type { ShapeTextRenderer } from '../edgeless/shape-text-renderer';
import { type FormatBrushData } from '../types';
import { Alignment, type IAlignment } from './alignment';
import { ArtText, type ArtTextCharacterConfig, type IArtText } from './art-text';
import {
    TextEffect,
    type BackgroundAdjustPayload,
    type GlowAdjustPayload,
    type ITextEffect,
    type ShadowAdjustPayload,
    type StrokeAdjustPayload,
    type TextEffectPreset,
} from './effect';
import { Fill, type IFill } from './fill';
import { FontFamily, type IFontFamily } from './font-family';
import { FontSize, type IFontSize } from './font-size';
import { FontStyle, type IFontStyle } from './font-style';
import { FormatBrush, type IFormatBrush } from './format-brush';
import { Highlight, type IHighlight } from './highlight';
import { ListText, type IListText } from './list-text';
import { TextMaterial, type ITextMaterial } from './material';
import { Opacity, type IOpacity } from './opacity';
import { Replacer, type IReplacer, type ReplaceLayerTextOptions, type SpecificTextStyle } from './replacer';
import { ShapeText, type IShapeText, type ShapeTextPropsOptions, type UnsetOptions } from './shape-text';
import { Shortcut, type IShortcut } from './shortcut';
import { Spacing, type ISpacing } from './spacing';
import { TextDecoration, type ITextDecoration } from './text-decoration';
import { WritingMode, type IWritingMode } from './writing-mode';

interface ITextLayer
    extends
        IShortcut,
        ITextMaterial,
        IShapeText,
        IFontFamily,
        IFontSize,
        IFontStyle,
        ITextDecoration,
        IWritingMode,
        ITextEffect,
        IListText,
        IAlignment,
        ISpacing,
        IHighlight,
        IFormatBrush,
        IReplacer,
        IOpacity,
        IArtText {
    setTextBrush(layerID: string, tip?: string, onApplied?: () => void): boolean;
}

export class TextLayer extends Layer implements ITextLayer {
    private shortcut: IShortcut;
    private textMaterial: ITextMaterial;
    private shapeText: IShapeText;
    private fontFamily: IFontFamily;
    private fontSize: IFontSize;
    private fontStyle: IFontStyle;
    private fill: IFill;
    private listText: IListText;
    private alignment: IAlignment;
    private spacing: ISpacing;
    private textDecoration: ITextDecoration;
    private writingMode: IWritingMode;
    private textEffect: ITextEffect;
    private highlight: IHighlight;
    private formatBrush: IFormatBrush;
    private replacer: IReplacer;
    private opacity: IOpacity;
    private artText: IArtText;

    constructor(identity: string, shapeTextRenderer?: ShapeTextRenderer) {
        super(identity);

        const framework = getFramework(identity);
        const renderer =
            shapeTextRenderer || framework.service.registration.getInstance<ShapeTextRenderer>('ShapeTextRenderer');
        if (!renderer) throw new Error('ShapeTextRenderer is not registered.');

        this.shortcut = new Shortcut(framework, this);
        this.textMaterial = new TextMaterial(framework, this);
        this.shapeText = new ShapeText(framework, renderer);
        this.fontFamily = new FontFamily(framework, this.shapeText);
        this.fontSize = new FontSize(framework, this.shapeText);
        this.fontStyle = new FontStyle(framework, this.shapeText);
        this.fill = new Fill(framework, this.shapeText);
        this.textDecoration = new TextDecoration(framework, this.shapeText);
        this.writingMode = new WritingMode(framework);
        this.listText = new ListText(framework);
        this.alignment = new Alignment(framework);
        this.spacing = new Spacing(framework, this.shapeText);
        this.textEffect = new TextEffect(framework, this.shapeText);
        this.highlight = new Highlight(framework);
        this.replacer = new Replacer(framework, this.shapeText);
        this.opacity = new Opacity(framework);
        this.artText = new ArtText(framework);
        this.formatBrush = new FormatBrush(framework, this.shapeText, this.artText);
    }

    /**
     * 获取文本选区
     *
     * - 未传 range 时默认使用当前编辑选区；
     * - 显式传 range: null 时按整层处理，不能被当前选区覆盖。
     * @param layerID - 图层 ID
     * @param range - 指定选区；undefined 表示使用当前编辑选区，null 表示整层
     * @returns 显式选区、当前编辑选区或表示整层的 null。
     */
    private resolveRange(layerID: string, range?: Optional<TextSelectionRange>) {
        if (range !== undefined) return range;

        const editor = this.framework.service.registration.getInstance<TextEditorPlugin>('TextEditorPlugin');
        if (!editor || editor.getEditingLayer()?.id !== layerID) return null;
        return editor.getSelectionRange();
    }

    /**
     * 创建默认文字，允许在目标画布插入前按其尺寸排版
     * @param partial - 文本样式和内容
     * @param position - 显式位置，覆盖默认居中位置
     * @param stageSize - 目标画布尺寸，省略时使用当前画布
     * @returns 未插入画布的文本图层
     */
    createDefaultTextLayer(partial: Partial<ITextLayerProps>, position?: Partial<IPosition>, stageSize?: ISize) {
        return this.shortcut.createDefaultTextLayer(partial, position, stageSize);
    }

    //#region shortcut
    /**
     * 添加默认标题，默认进入编辑态并全选文本
     * @param options - 添加选项；byMousePosition 控制鼠标定位，selectAll 默认为 true，false 时只添加图层
     * @returns 添加后的文本图层
     */
    addDefaultTitle(options?: { byMousePosition?: boolean; selectAll?: boolean }) {
        const layer = this.shortcut.addDefaultTitle({ byMousePosition: options?.byMousePosition });
        if (options?.selectAll ?? true) {
            const editor = this.framework.service.registration.getInstance<TextEditorPlugin>('TextEditorPlugin');
            void editor?.enter(layer.id, { selectAll: true });
        }
        return layer;
    }

    /**
     * 添加默认子标题，默认进入编辑态并全选文本
     * @param options - 添加选项；byMousePosition 控制鼠标定位，selectAll 默认为 true，false 时只添加图层
     * @returns 添加后的文本图层
     */
    addDefaultSubTitle(options?: { byMousePosition?: boolean; selectAll?: boolean }) {
        const layer = this.shortcut.addDefaultSubTitle({ byMousePosition: options?.byMousePosition });
        if (options?.selectAll ?? true) {
            const editor = this.framework.service.registration.getInstance<TextEditorPlugin>('TextEditorPlugin');
            void editor?.enter(layer.id, { selectAll: true });
        }
        return layer;
    }

    /**
     * 添加默认正文，默认进入编辑态并全选文本
     * @param options - 添加选项；byMousePosition 控制鼠标定位，selectAll 默认为 true，false 时只添加图层
     * @returns 添加后的文本图层
     */
    addDefaultContent(options?: { byMousePosition?: boolean; selectAll?: boolean }) {
        const layer = this.shortcut.addDefaultContent({ byMousePosition: options?.byMousePosition });
        if (options?.selectAll ?? true) {
            const editor = this.framework.service.registration.getInstance<TextEditorPlugin>('TextEditorPlugin');
            void editor?.enter(layer.id, { selectAll: true });
        }
        return layer;
    }

    addDefaultEffectText() {
        return this.shortcut.addDefaultEffectText();
    }

    addDefaultShapeText(materialID: string) {
        return this.shortcut.addDefaultShapeText(materialID);
    }

    /**
     * 创建自定义文字图层，可通过宽度上限约束粘贴长文本的换行
     *
     * @param layerData - 自定义文字内容和样式
     * @param options - 插入位置选项及文字框宽度上限
     * @returns 插入后的文字图层
     */
    addWithCustomLayerData(
        layerData: Partial<ITextLayerProps>,
        options: { byMousePosition?: boolean; maxWidth?: number } = {},
    ) {
        return this.shortcut.addWithCustomLayerData(layerData, options);
    }
    //#endregion

    //#region material
    storeMaterialLayerToCache(materialID: string, serveLayer: IServerGroupLayerProps, version?: string) {
        return this.textMaterial.storeMaterialLayerToCache(materialID, serveLayer, version);
    }

    loadMaterialLayerFromCache(materialID: string) {
        return this.textMaterial.loadMaterialLayerFromCache(materialID);
    }

    addByMaterialLayer(layer: ILayerProps, byMousePosition?: boolean) {
        return this.textMaterial.addByMaterialLayer(layer, byMousePosition);
    }
    // #endregion

    // #region shape-text
    loadShapeTextAssets() {
        return this.shapeText.loadShapeTextAssets();
    }

    applyShapeText(
        layerID: string,
        shapeType: TextShapeType,
        options?: { materialID?: number; params?: IShapeExtension },
    ) {
        return this.shapeText.applyShapeText(layerID, shapeType, options);
    }

    cancelShapeEffect(layerID: string, options?: UnsetOptions) {
        return this.shapeText.cancelShapeEffect(layerID, options);
    }

    adjustShapeTextProps(layerID: string, key: string, value: number | boolean, options: ShapeTextPropsOptions) {
        return this.shapeText.adjustShapeTextProps(layerID, key, value, options);
    }

    refreshShapeTextRender(layer: ILayerProps) {
        return this.shapeText.refreshShapeTextRender(layer);
    }

    // #endregion

    // #region font-base
    setFontFamily(layerID: string, fontFamily: string, options?: { range?: Optional<TextSelectionRange> }) {
        return this.fontFamily.setFontFamily(layerID, fontFamily, {
            range: this.resolveRange(layerID, options?.range),
        });
    }

    setFontSize(
        layerID: string,
        fontSize: number,
        options?: { range?: Optional<TextSelectionRange>; writeHistory?: boolean },
    ) {
        return this.fontSize.setFontSize(layerID, fontSize, {
            ...options,
            range: this.resolveRange(layerID, options?.range),
        });
    }

    setItalicStyle(layerID: string, flag: boolean, options?: { range?: Optional<TextSelectionRange> }) {
        return this.fontStyle.setItalicStyle(layerID, flag, { range: this.resolveRange(layerID, options?.range) });
    }

    setBoldStyle(layerID: string, flag: boolean, options?: { range?: Optional<TextSelectionRange> }) {
        return this.fontStyle.setBoldStyle(layerID, flag, { range: this.resolveRange(layerID, options?.range) });
    }
    // #endregion

    // #region text-color
    setFillColor(
        layerID: string,
        color: string,
        options?: { range?: Optional<TextSelectionRange>; writeHistory?: boolean },
    ) {
        return this.fill.setFillColor(layerID, color, {
            ...options,
            range: this.resolveRange(layerID, options?.range),
        });
    }

    setFillGradient(layerID: string, gradient: string, options?: { writeHistory?: boolean }) {
        return this.fill.setFillGradient(layerID, gradient, options);
    }

    setFillTexture(layerID: string, texture: ITexture) {
        return this.fill.setFillTexture(layerID, texture);
    }
    // #endregion

    // #region list-text
    applyListText(layerID: string, data: ListStyleStruct) {
        return this.listText.applyListText(layerID, data);
    }

    cancelListText(layerID: string) {
        return this.listText.cancelListText(layerID);
    }

    updateListStyleMaterial(
        layerID: string,
        data: IListStyleMaterial,
        options?: { writeHistory?: boolean },
    ): ILayerProps | void {
        return this.listText.updateListStyleMaterial(layerID, data, options);
    }
    // #endregion

    // #region text-decoration
    setUnderline(layerID: string, flag: boolean, options?: { range?: Optional<TextSelectionRange> }) {
        return this.textDecoration.setUnderline(layerID, flag, { range: this.resolveRange(layerID, options?.range) });
    }

    setLineThrough(layerID: string, flag: boolean, options?: { range?: Optional<TextSelectionRange> }) {
        return this.textDecoration.setLineThrough(layerID, flag, { range: this.resolveRange(layerID, options?.range) });
    }
    // #endregion

    // #region writing-mode
    setWritingMode(layerID: string, writingMode: TextWritingType) {
        return this.writingMode.setWritingMode(layerID, writingMode);
    }
    // #endregion

    // #region alignment
    setTextAlign(layerID: string, align: TextAlignType) {
        return this.alignment.setTextAlign(layerID, align);
    }
    // #endregion

    // #region spacing
    setLineSpacing(layerID: string, value: number, options?: { writeHistory?: boolean }) {
        return this.spacing.setLineSpacing(layerID, value, options);
    }

    setLetterSpacing(layerID: string, value: number, options?: { writeHistory?: boolean }) {
        return this.spacing.setLetterSpacing(layerID, value, options);
    }
    // #endregion

    // #region text-effect
    addStroke(layerID: string, options?: { color?: string }) {
        return this.textEffect.addStroke(layerID, options);
    }

    addShadow(layerID: string, options?: { color?: string }) {
        return this.textEffect.addShadow(layerID, options);
    }

    addGlow(layerID: string, options?: { color?: string }) {
        return this.textEffect.addGlow(layerID, options);
    }

    addBackground(layerID: string, options?: { color?: string }) {
        return this.textEffect.addBackground(layerID, options);
    }

    setEffectVisibility(layerID: string, effect: TextEffectKind, flag: boolean, index?: number) {
        return this.textEffect.setEffectVisibility(layerID, effect, flag, index);
    }

    removeEffect(layerID: string, effect: TextEffectKind, index?: number) {
        return this.textEffect.removeEffect(layerID, effect, index);
    }

    clearAllEffects(layerID: string) {
        return this.textEffect.clearAllEffects(layerID);
    }

    applyEffectPreset(layerID: string, preset: TextEffectPreset) {
        return this.textEffect.applyEffectPreset(layerID, preset);
    }

    adjustStrokeProps(layerID: string, payload: StrokeAdjustPayload, options?: { writeHistory?: boolean }) {
        return this.textEffect.adjustStrokeProps(layerID, payload, options);
    }

    adjustShadowProps(layerID: string, payload: ShadowAdjustPayload, options?: { writeHistory?: boolean }) {
        return this.textEffect.adjustShadowProps(layerID, payload, options);
    }

    adjustGlowProps(layerID: string, payload: GlowAdjustPayload, options?: { writeHistory?: boolean }) {
        return this.textEffect.adjustGlowProps(layerID, payload, options);
    }

    adjustBackgroundProps(layerID: string, payload: BackgroundAdjustPayload, options?: { writeHistory?: boolean }) {
        return this.textEffect.adjustBackgroundProps(layerID, payload, options);
    }
    // #endregion

    // #region highlight
    setHighlight(layerID: string, value: ITextMark, options?: { range?: Optional<TextSelectionRange> }) {
        return this.highlight.setHighlight(layerID, value, {
            range: this.resolveRange(layerID, options?.range),
        });
    }

    removeHighlight(layerID: string, options?: { range?: Optional<TextSelectionRange> }) {
        return this.highlight.removeHighlight(layerID, {
            range: this.resolveRange(layerID, options?.range),
        });
    }

    updateHighlight(
        layerID: string,
        value: ITextMark,
        options?: { writeHistory?: boolean; range?: Optional<TextSelectionRange> },
    ) {
        return this.highlight.updateHighlight(layerID, value, {
            ...options,
            range: this.resolveRange(layerID, options?.range),
        });
    }
    // #endregion

    // #region format-brush
    /** 提取当前文字格式并开启文字格式刷。 */
    setTextBrush(layerID: string, tip?: string, onApplied?: () => void) {
        const implementation: FormatBrushImplementation<FormatBrushData> = {
            targetLayerType: LayerType.TEXT_LAYER,
            extract: (sourceLayerId) => this.extractFormatBrushData(sourceLayerId),
            apply: async (targetLayerId, context, data) => {
                const applied = Boolean(await this.applyFormatBrush(targetLayerId, context, data));
                if (applied && onApplied) {
                    try {
                        onApplied();
                    } catch (error) {
                        this.framework.service.captureException(
                            error instanceof Error ? error : new Error(String(error)),
                            {
                                extra: {
                                    sourceLayerId: layerID,
                                    targetLayerId,
                                    formatBrushTargetLayerType: LayerType.TEXT_LAYER,
                                    phase: 'on-applied',
                                },
                            },
                        );
                    }
                }
                return applied;
            },
            dispose: (data) => this.clearFormatBrushData(data),
        };
        return this.framework.service.formatBrush.start({
            sourceLayerId: layerID,
            cursorTip: tip,
            implementation,
        });
    }

    extractFormatBrushData(layerID: string) {
        return this.formatBrush.extractFormatBrushData(layerID);
    }

    applyFormatBrush(layerID: string, context: FormatBrushApplyContext, formatBrushData?: FormatBrushData) {
        return this.formatBrush.applyFormatBrush(layerID, context, formatBrushData);
    }

    clearFormatBrushData(formatBrushData?: FormatBrushData) {
        this.formatBrush.clearFormatBrushData(formatBrushData);
    }
    // #endregion

    // #region replace
    replaceLayers(prevLayers: ILayerProps[], nextLayers: ILayerProps[], historyFlag?: string) {
        return this.replacer.replaceLayers(prevLayers, nextLayers, historyFlag);
    }

    replaceLayerText(layer: ILayerProps, text: string, options?: ReplaceLayerTextOptions) {
        return this.replacer.replaceLayerText(layer, text, options);
    }

    updateBySpecificStyle(layerID: string, style: SpecificTextStyle, fontMaterialID?: number) {
        return this.replacer.updateBySpecificStyle(layerID, style, fontMaterialID);
    }
    // #endregion

    // #region opacity
    setLayerOpacity(layerID: string, opacity: number, options?: { writeHistory?: boolean }) {
        return this.opacity.setLayerOpacity(layerID, opacity, options);
    }
    // #endregion

    // #region art-text
    /**
     * 应用 Advanced Art Text。
     *
     * @param layerID - 图层 ID。
     * @param data - 花字素材配置。
     * @param options - 历史写入选项。
     * @returns 应用完成后的图层或无返回值。
     */
    applyArtText(layerID: string, data: IAdvancedTextMaterial, options = { writeHistory: true }) {
        return this.artText.applyArtText(layerID, data, options);
    }

    /**
     * 取消 Advanced Art Text。
     *
     * @param layerID - 图层 ID。
     * @param options - 历史写入选项。
     * @returns 更新后的图层或无返回值。
     */
    cancelArtText(layerID: string, options?: { writeHistory?: boolean }) {
        return this.artText.cancelArtText(layerID, options);
    }

    /**
     * 调整当前选区命中的 Advanced Art Text 重复位。
     *
     * 花字调整只使用调用方传入的结构化选区；未传时按整层处理。
     *
     * @param layerID - 图层 ID。
     * @param partialConfig - 字符属性补丁。
     * @param options - 历史与可选显式选区。
     * @returns 更新后的图层或无返回值。
     */
    adjustArtTextSingleCharacter(
        layerID: string,
        partialConfig: ArtTextCharacterConfig,
        options?: { writeHistory?: boolean; range?: Optional<TextSelectionRange> },
    ): ILayerProps | void {
        return this.artText.adjustArtTextSingleCharacter(layerID, partialConfig, {
            ...options,
            range: options?.range ?? null,
        });
    }
    // #endregion

    /**
     * polyfill Intl.Segmenter
     */
    async polyfill() {
        if (shouldPolyfill()) {
            await import('@formatjs/intl-segmenter/polyfill-force');
        }
    }
}
